import React from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles } from "lucide-react";
import AnimatedCounter from "@/components/AnimatedCounter";
import { STATS, BRAND_LOGOS } from "@/lib/mockData";

const Hero = () => {
  return (
    <section className="relative overflow-hidden">
      {/* radial glow + grid */}
      <div className="absolute inset-0 bg-radial-blue pointer-events-none" />
      <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none [mask-image:radial-gradient(ellipse_at_top,black_30%,transparent_70%)]" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 lg:pt-28 lg:pb-32">
        <div className="flex items-center gap-2 text-xs text-zinc-400 mb-6 animate-fade-up">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-white/[0.03]">
            <Sparkles className="h-3.5 w-3.5 text-blue-400" />
            India's most outcome-obsessed creator agency
          </span>
        </div>

        <h1
          className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter-2 text-zinc-50 text-balance max-w-4xl animate-fade-up"
          style={{ animationDelay: "60ms" }}
          data-testid="hero-headline"
        >
          We connect brands to{" "}
          <span className="text-blue-500">creators that actually convert</span>.
        </h1>

        <p
          className="mt-6 text-base sm:text-lg text-zinc-400 max-w-2xl leading-relaxed animate-fade-up"
          style={{ animationDelay: "140ms" }}
          data-testid="hero-subheadline"
        >
          Bagfluence is the operating system behind India's sharpest creator campaigns —
          discovery, vetting, execution, and investor-grade reporting, end to end.
        </p>

        <div
          className="mt-9 flex flex-wrap items-center gap-3 animate-fade-up"
          style={{ animationDelay: "220ms" }}
        >
          <a href="#contact">
            <Button
              size="lg"
              className="bg-blue-600 hover:bg-blue-500 text-white rounded-md px-6 h-12 glow-blue"
              data-testid="hero-primary-cta"
            >
              Book a Free Strategy Call
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </a>
          <a href="#case-studies">
            <Button
              size="lg"
              variant="outline"
              className="rounded-md px-6 h-12 bg-transparent border-zinc-700 text-zinc-200 hover:bg-white/5 hover:text-white"
              data-testid="hero-secondary-cta"
            >
              See Our Work
            </Button>
          </a>
        </div>

        {/* Trust marquee */}
        <div
          className="mt-14 animate-fade-up"
          style={{ animationDelay: "300ms" }}
          data-testid="hero-trust-marquee"
        >
          <p className="text-xs uppercase tracking-[0.18em] text-zinc-500 mb-5">
            Trusted by India's most-loved brands
          </p>
          <div className="relative overflow-hidden">
            <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-zinc-950 to-transparent z-10" />
            <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-zinc-950 to-transparent z-10" />
            <div className="flex animate-marquee gap-12 w-max">
              {[...BRAND_LOGOS, ...BRAND_LOGOS].map((b, i) => (
                <div
                  key={`${b}-${i}`}
                  className="text-zinc-500 text-2xl font-semibold tracking-tight whitespace-nowrap hover:text-zinc-200 transition-colors"
                >
                  {b}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div
          className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 animate-fade-up"
          style={{ animationDelay: "360ms" }}
          data-testid="hero-stats"
        >
          {STATS.map((s, i) => (
            <div
              key={s.label}
              className="rounded-xl border border-white/5 bg-white/[0.02] backdrop-blur-sm p-5 lg:p-6"
            >
              <div className="text-3xl lg:text-4xl font-semibold tracking-tighter text-zinc-50">
                <AnimatedCounter
                  value={s.value}
                  prefix={s.prefix || ""}
                  suffix={s.suffix || ""}
                  decimals={s.decimals || 0}
                  data-testid={`stat-value-${i}`}
                />
              </div>
              <div className="text-xs uppercase tracking-wider text-zinc-500 mt-2">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
